import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { format, isSameMonth, isToday } from 'date-fns';
import { cn } from '../../utils/cn';
import type { CalendarEvent } from '../../types/calendar';
import type { CalendarLinkSummary } from '../../services/calendarLinkSources';
import { dayKey } from '../../utils/calendarEvents';
import { hiddenCountsByColumn, layoutWeeks, type EventSpan } from '../../utils/calendarLayout';
import { DayEventsPopover } from './DayEventsPopover';
import { EventBar } from './EventBar';
import { eventColor, type TagColorLookup } from './eventColors';

export interface MonthGridProps {
  events: readonly CalendarEvent[];
  /** The month being shown; days outside it are dimmed. */
  currentDate: Date;
  /** First day of the first row and last day of the last row. */
  gridStart: Date;
  gridEnd: Date;
  tagColors: TagColorLookup;
  links: ReadonlyMap<string, CalendarLinkSummary>;
  onEventClick: (event: CalendarEvent) => void;
  /** Rows show at most this many lanes; the rest collapse into "+N more". */
  maxLanes: number;
  /** Shorter rows for narrow screens. */
  compact?: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_ROW = '1.75rem';
const LANE_ROW = '1.625rem';
const MORE_ROW = 'minmax(1.25rem, auto)';

interface OpenDay {
  key: string;
  day: Date;
  anchor: HTMLElement;
}

/**
 * Month view: one card, seven columns, and each week laid out as a row of
 * lanes so an event that runs for several days is one continuous bar rather
 * than a copy in every cell. Timed events sit beneath the bars as chips.
 */
export const MonthGrid = ({
  events,
  currentDate,
  gridStart,
  gridEnd,
  tagColors,
  links,
  onEventClick,
  maxLanes,
  compact = false,
}: MonthGridProps) => {
  const [openDay, setOpenDay] = useState<OpenDay | null>(null);

  const weeks = useMemo(() => layoutWeeks(events, gridStart, gridEnd), [events, gridStart, gridEnd]);

  // Everything touching each day, for the popover.
  const spansByDay = useMemo(() => {
    const byDay = new Map<string, EventSpan[]>();
    for (const week of weeks) {
      for (const segment of week.segments) {
        for (let col = segment.startCol; col <= segment.endCol; col++) {
          const key = dayKey(week.days[col]);
          const list = byDay.get(key);
          if (list) list.push(segment.span);
          else byDay.set(key, [segment.span]);
        }
      }
    }
    for (const list of byDay.values()) list.sort((a, b) => a.start.getTime() - b.start.getTime());
    return byDay;
  }, [weeks]);

  const closeDay = useCallback(() => setOpenDay(null), []);

  const toggleDay = (day: Date, event: MouseEvent<HTMLElement>) => {
    const key = dayKey(day);
    setOpenDay(current => (current?.key === key ? null : { key, day, anchor: event.currentTarget }));
  };

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-glass-200/10">
        {WEEKDAYS.map(name => (
          <div key={name} className="eyebrow py-2 text-center">
            {compact ? name.slice(0, 1) : name}
          </div>
        ))}
      </div>

      {weeks.map((week, weekIndex) => {
        const visibleLanes = Math.min(week.laneCount, maxLanes);
        const hidden = hiddenCountsByColumn(week, visibleLanes);
        const isLastWeek = weekIndex === weeks.length - 1;
        const rows = [DAY_ROW, ...Array<string>(visibleLanes).fill(LANE_ROW), MORE_ROW].join(' ');

        return (
          <div
            key={week.weekStart.toISOString()}
            className={cn('grid grid-cols-7', compact ? 'min-h-[4.75rem]' : 'min-h-[7.25rem]', !isLastWeek && 'border-b border-glass-200/10')}
            style={{ gridTemplateRows: rows }}
          >
            {week.days.map((day, col) => {
              const key = dayKey(day);
              const inMonth = isSameMonth(day, currentDate);
              const today = isToday(day);
              const count = spansByDay.get(key)?.length ?? 0;
              const numberClass = cn(
                'w-6 h-6 rounded-full text-[11px] tabular-nums flex items-center justify-center transition-colors',
                today ? 'bg-accent-500 text-on-solid font-semibold' : inMonth ? 'text-glass-300' : 'text-glass-500',
              );
              return (
                <div
                  key={key}
                  className={cn(
                    'min-w-0 px-1 pt-1 pb-1',
                    col < 6 && 'border-r border-glass-200/10',
                    !inMonth && 'bg-dark-900/20',
                    today && 'bg-accent-500/[0.06]',
                  )}
                  style={{ gridColumn: col + 1, gridRow: '1 / -1' }}
                >
                  {count > 0 ? (
                    <button
                      type="button"
                      onClick={event => toggleDay(day, event)}
                      className={cn(numberClass, 'hover:bg-glass-200/15 hover:text-white')}
                      title={`${count} ${count === 1 ? 'event' : 'events'} on ${format(day, 'EEEE d MMMM')}`}
                      aria-label={`Show ${count} ${count === 1 ? 'event' : 'events'} on ${format(day, 'EEEE d MMMM')}`}
                    >
                      {format(day, 'd')}
                    </button>
                  ) : (
                    <span className={numberClass}>{format(day, 'd')}</span>
                  )}
                </div>
              );
            })}

            {week.segments
              .filter(segment => segment.lane < visibleLanes)
              .map(segment => (
                <EventBar
                  key={`${segment.span.event.id}:${segment.startCol}`}
                  span={segment.span}
                  color={eventColor(segment.span.event, tagColors)}
                  linked={links.get(segment.span.event.id)}
                  continuesBefore={segment.continuesBefore}
                  continuesAfter={segment.continuesAfter}
                  showTime={!compact}
                  onClick={onEventClick}
                  className={cn('self-center', segment.continuesBefore ? 'ml-0' : 'ml-1', segment.continuesAfter ? 'mr-0' : 'mr-1')}
                  style={{
                    gridColumn: `${segment.startCol + 1} / span ${segment.endCol - segment.startCol + 1}`,
                    gridRow: segment.lane + 2,
                  }}
                />
              ))}

            {hidden.map((count, col) =>
              count > 0 ? (
                <button
                  key={`more-${col}`}
                  type="button"
                  onClick={event => toggleDay(week.days[col], event)}
                  className="justify-self-start self-start mx-1 h-5 px-1 rounded text-[11px] text-glass-400 hover:text-white hover:bg-glass-200/10 transition-colors"
                  style={{ gridColumn: col + 1, gridRow: visibleLanes + 2 }}
                >
                  +{count} more
                </button>
              ) : null,
            )}
          </div>
        );
      })}

      {openDay && (
        <DayEventsPopover
          day={openDay.day}
          spans={spansByDay.get(openDay.key) ?? []}
          anchor={openDay.anchor}
          tagColors={tagColors}
          links={links}
          onEventClick={onEventClick}
          onClose={closeDay}
        />
      )}
    </div>
  );
};
